// Games-played history: date, size, opponent, result. Click a finished game
// to review it; click a live one to keep playing.

import { useEffect, useState } from 'react';
import type { GameSummary } from '../../shared/types.ts';
import { api, errorMessage } from '../api.ts';
import { navigate } from '../router.ts';

function opponentLabel(g: GameSummary): string {
  if (g.mode === 'hotseat') return 'Hotseat';
  const you = g.humanColor === 'B' ? 'Black' : g.humanColor === 'W' ? 'White' : '?';
  return `GNU Go L${g.botLevel ?? '?'} · you were ${you}`;
}

function statusLabel(g: GameSummary): string {
  // A drawn game persists result 'Draw' (winner null) and renders as such.
  if (g.status === 'finished') return g.result ?? 'Finished';
  if (g.status === 'scoring') return 'Scoring';
  return 'In progress';
}

function statusClass(g: GameSummary): string {
  if (g.status === 'scoring') return 'is-scoring';
  if (g.status !== 'finished') return 'is-live';
  return g.mode === 'bot' && g.winner !== null && g.winner === g.humanColor
    ? 'is-win'
    : 'is-finished';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function HistoryScreen() {
  const [games, setGames] = useState<GameSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .listGames()
      .then((r) => {
        if (alive) setGames(r.games);
      })
      .catch((err) => {
        if (alive) setError(errorMessage(err));
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <main className="history-screen">
      <div className="history-card">
        <header className="history-head">
          <h1>Past games</h1>
          <button type="button" className="btn btn-accent" onClick={() => navigate('/')}>
            New game
          </button>
        </header>

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        {games !== null && games.length === 0 && (
          <div className="history-empty">
            <p>No games yet.</p>
            <p className="empty-note">
              Your first game is a blank board — <a href="#/">begin one</a> and it will appear
              here.
            </p>
          </div>
        )}

        {games !== null && games.length > 0 && (
          <ul className="history-list">
            {games.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  className="history-row"
                  onClick={() =>
                    navigate(g.status === 'finished' ? `/review/${g.id}` : `/game/${g.id}`)
                  }
                >
                  <span className="history-primary">
                    <span className="history-opponent">{opponentLabel(g)}</span>
                    <span className="history-meta">
                      <time dateTime={g.createdAt}>{formatDate(g.createdAt)}</time>
                      <span aria-hidden="true">·</span>
                      <span className="mono">{g.boardSize}×{g.boardSize}</span>
                      <span aria-hidden="true">·</span>
                      <span>{g.moveCount} moves</span>
                    </span>
                  </span>
                  <span className={`history-status ${statusClass(g)}`}>
                    <span className="status-dot" aria-hidden="true" />
                    <span className="mono">{statusLabel(g)}</span>
                  </span>
                  <span className="history-open" aria-hidden="true">
                    {g.status === 'finished' ? 'Review' : 'Continue'} <span>→</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {games === null && !error && <p className="empty-note">Loading…</p>}
      </div>
    </main>
  );
}
